import { useState, useEffect, useRef } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import BN from 'bn.js'
import {
  X,
  Plus,
  Target,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Info,
  Tag,
  Hash,
  User,
  ArrowRight
} from 'lucide-react'
import { cn } from '../../lib/utils'

/**
 * Props for the CreateProposalModal component
 * V2: Updated to include title and description parameters
 */
interface CreateProposalModalProps {
  isOpen: boolean
  onClose: () => void
  /** V2: onCreateProposal signature includes title, description, amount, and destinationAddress */
  onCreateProposal: (title: string, description: string, requestedAmount: BN, destinationAddress: string) => Promise<void>
  tokenSymbol?: string
  loading?: boolean
  error?: string | null
}

/**
 * Interface for proposal form data
 * Simplified to remove category as we only support Funding/Payout proposals
 */
interface ProposalData {
  title: string
  description: string
  recipient: string
  amount: number
  priority: 'low' | 'medium' | 'high' | 'critical'
  tags: string[]
}

/**
 * Enhanced CreateProposalModal Component - Premium glassmorphism proposal creation interface
 * 
 * This component provides:
 * - Professional glassmorphism modal design
 * - Simplified 2-step form (Details -> Review)
 * - Real-time input validation and feedback
 * - Priority selection
 * - Tag system for proposal organization
 * - Responsive design optimized for all devices
 * - Accessibility-compliant form structure
 * - Premium DeFi platform aesthetics
 */
export function CreateProposalModal({
  isOpen,
  onClose,
  onCreateProposal,
  tokenSymbol = 'SOL',
  loading = false,
  error = null
}: CreateProposalModalProps): JSX.Element | null {
  // Step 1: Details, Step 2: Review
  const [step, setStep] = useState(1)
  
  const [formData, setFormData] = useState<ProposalData>({
    title: '',
    description: '',
    recipient: '',
    amount: 0,
    priority: 'medium',
    tags: []
  })
  
  const [confirming, setConfirming] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof ProposalData, string>>>({})
  const [tagInput, setTagInput] = useState('')
  const titleInputRef = useRef<HTMLInputElement>(null)
  const { connected } = useWallet()

  /**
   * Priority options with colors
   */
  const priorities = [
    { value: 'low' as const, label: 'Low', color: 'text-text-muted' },
    { value: 'medium' as const, label: 'Medium', color: 'text-cyber-yellow' },
    { value: 'high' as const, label: 'High', color: 'text-cyber-magenta' },
    { value: 'critical' as const, label: 'Critical', color: 'text-status-error' }
  ]

  /**
   * Validate form data
   * Checks all required fields before proceeding
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProposalData, string>> = {}

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters'
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters'
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters'
    } else if (formData.description.length > 2000) {
      newErrors.description = 'Description must be less than 2000 characters'
    }

    // Recipient validation (Always required for Payout proposals)
    if (!formData.recipient.trim()) {
      newErrors.recipient = 'Recipient address is required'
    } else if (formData.recipient.length < 32) {
      newErrors.recipient = 'Invalid recipient address'
    }

    // Amount validation (Always required for Payout proposals)
    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form field changes
   */
  const handleFieldChange = <K extends keyof ProposalData>(
    field: K,
    value: ProposalData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  /**
   * Add tag to proposal
   */
  const addTag = () => {
    const tag = tagInput.trim().toLowerCase()
    if (tag && !formData.tags.includes(tag) && formData.tags.length < 5) {
      handleFieldChange('tags', [...formData.tags, tag])
      setTagInput('')
    }
  }

  /**
   * Remove tag from proposal
   */
  const removeTag = (tagToRemove: string) => {
    handleFieldChange('tags', formData.tags.filter(tag => tag !== tagToRemove))
  }

  /**
   * Handle tag input key press
   */
  const handleTagKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag()
    }
  }

  /**
   * Go to next step
   * Validates form before proceeding
   */
  const nextStep = () => {
    if (step === 1) {
      if (validateForm()) {
        setStep(2)
      }
    }
  }

  /**
   * Go to previous step
   */
  const previousStep = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  /**
   * Handle proposal submission
   * V2: Now passes title, description, amount, and destination address to match contract
   */
  const handleSubmit = async () => {
    if (!validateForm() || confirming) return

    setConfirming(true)
    try {
      // Convert amount to lamports (BN)
      const lamports = new BN(formData.amount * 1e9)
      
      // V2: Pass all proposal data - title, description, amount, and destination address
      await onCreateProposal(formData.title, formData.description, lamports, formData.recipient)
      handleClose()
    } catch (error) {
      console.error('Proposal creation failed:', error)
    } finally {
      setConfirming(false)
    }
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    if (confirming) return
    onClose()
    // Reset state after a short delay to allow animation to finish
    setTimeout(() => {
      setStep(1)
      setFormData({
        title: '',
        description: '',
        recipient: '',
        amount: 0,
        priority: 'medium',
        tags: []
      })
      setErrors({})
      setTagInput('')
    }, 300)
  }

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  // Auto-focus title input when modal opens
  useEffect(() => {
    if (isOpen && step === 1 && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, step])

  if (!isOpen) return null

  const selectedPriority = priorities.find(p => p.value === formData.priority)!

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9999] bg-dark-bg/60 backdrop-blur-sm animate-backdrop-in"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="modal-content w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-modal-in"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-labelledby="create-proposal-modal-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-dark-border/30">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-cyber-gradient rounded-cyber flex items-center justify-center">
                <Plus className="w-5 h-5 text-dark-bg" />
              </div>
              <div>
                <h3 id="create-proposal-modal-title" className="text-xl font-header text-gradient-cyber">
                  Create Proposal
                </h3>
                <p className="text-sm text-text-muted">
                  Step {step} of 2 - {step === 1 ? 'Proposal Details' : 'Review & Submit'}
                </p>
              </div>
            </div>
            
            <button
              onClick={handleClose}
              disabled={confirming}
              className="p-2 rounded-cyber hover:bg-dark-panel/50 transition-colors duration-200 disabled:opacity-50"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-text-muted" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 pt-4">
            <div className="progress-bar h-1">
              <div 
                className="progress-fill h-full transition-all duration-300"
                style={{ width: `${(step / 2) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Error Display */}
            {error && (
              <div className="glass-card bg-status-error/10 border-status-error/20 p-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-status-error flex-shrink-0" />
                  <span className="text-status-error text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Step 1: Proposal Details */}
            {step === 1 && (
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label htmlFor="proposal-title" className="block text-sm font-medium text-text-secondary mb-2">
                    Proposal Title *
                  </label>
                  <input
                    id="proposal-title"
                    ref={titleInputRef}
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    placeholder="Enter a clear, descriptive title..."
                    className={cn(
                      "cyber-input w-full",
                      errors.title && "border-status-error focus:border-status-error"
                    )}
                    maxLength={100}
                  />
                  {errors.title && (
                    <div className="mt-1 text-status-error text-sm flex items-center space-x-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{errors.title}</span>
                    </div>
                  )}
                  <div className="mt-1 text-xs text-text-muted text-right">
                    {formData.title.length}/100
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="proposal-description" className="block text-sm font-medium text-text-secondary mb-2">
                    Description *
                  </label>
                  <textarea
                    id="proposal-description"
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    placeholder="Provide a detailed description of your proposal, including objectives, implementation plan, and expected outcomes..."
                    rows={6}
                    className={cn(
                      "cyber-input w-full resize-none",
                      errors.description && "border-status-error focus:border-status-error"
                    )}
                    maxLength={2000}
                  />
                  {errors.description && (
                    <div className="mt-1 text-status-error text-sm flex items-center space-x-1">
                      <AlertTriangle className="w-3 h-3" />
                      <span>{errors.description}</span>
                    </div>
                  )}
                  <div className="mt-1 text-xs text-text-muted text-right">
                    {formData.description.length}/2000
                  </div>
                </div>

                {/* Recipient & Amount Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Recipient */}
                  <div>
                    <label htmlFor="proposal-recipient" className="block text-sm font-medium text-text-secondary mb-2">
                      Recipient Address *
                    </label>
                    <div className="relative">
                      <input
                        id="proposal-recipient"
                        type="text"
                        value={formData.recipient}
                        onChange={(e) => handleFieldChange('recipient', e.target.value)}
                        placeholder="Enter Solana wallet address..."
                        className={cn(
                          "cyber-input w-full pl-10",
                          errors.recipient && "border-status-error focus:border-status-error"
                        )}
                      />
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
                    </div>
                    {errors.recipient && (
                      <div className="mt-1 text-status-error text-sm flex items-center space-x-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{errors.recipient}</span>
                      </div>
                    )}
                  </div>

                  {/* Amount */}
                  <div>
                    <label htmlFor="proposal-amount" className="block text-sm font-medium text-text-secondary mb-2">
                      Requested Amount ({tokenSymbol}) *
                    </label>
                    <div className="relative">
                      <input
                        id="proposal-amount"
                        type="number"
                        step="0.0001"
                        min="0"
                        value={formData.amount || ''}
                        onChange={(e) => handleFieldChange('amount', parseFloat(e.target.value) || 0)}
                        placeholder="0.0000"
                        className={cn(
                          "cyber-input w-full pl-10",
                          errors.amount && "border-status-error focus:border-status-error"
                        )}
                      />
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
                    </div>
                    {errors.amount && (
                      <div className="mt-1 text-status-error text-sm flex items-center space-x-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span>{errors.amount}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-3">
                    Priority Level
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {priorities.map((priority) => (
                      <button
                        key={priority.value}
                        onClick={() => handleFieldChange('priority', priority.value)}
                        className={cn(
                          "p-3 rounded-cyber border transition-all duration-300 text-center",
                          formData.priority === priority.value
                            ? "border-cyber-cyan bg-cyber-cyan/10"
                            : "border-dark-border hover:border-cyber-cyan/50 glass-dark"
                        )}
                      >
                        <div className={cn(
                          "font-medium text-sm",
                          formData.priority === priority.value ? "text-cyber-cyan" : priority.color
                        )}>
                          {priority.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Tags (Optional)
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagKeyPress}
                          placeholder="Add tags to categorize your proposal..."
                          className="cyber-input w-full pl-10"
                          maxLength={20}
                        />
                        <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
                      </div>
                      <button
                        onClick={addTag}
                        disabled={!tagInput.trim() || formData.tags.length >= 5}
                        className="cyber-button-secondary px-4 py-2 disabled:opacity-50"
                      >
                        Add
                      </button>
                    </div>
                    
                    {formData.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag) => (
                          <button
                            key={tag}
                            onClick={() => removeTag(tag)}
                            className="flex items-center space-x-1 px-2 py-1 bg-cyber-cyan/20 text-cyber-cyan rounded text-sm hover:bg-cyber-cyan/30 transition-colors duration-200"
                          >
                            <Hash className="w-3 h-3" />
                            <span>{tag}</span>
                            <X className="w-3 h-3 hover:text-status-error" />
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <div className="text-xs text-text-muted">
                      {formData.tags.length}/5 tags used
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Review & Submit */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="glass-card p-6 space-y-4">
                  <h4 className="text-lg font-header text-cyber-cyan flex items-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>Review Your Proposal</span>
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-text-muted">Title</div>
                      <div className="text-text-primary font-medium">{formData.title}</div>
                    </div>

                    <div>
                      <div className="text-sm text-text-muted">Type</div>
                      <div className="flex items-center space-x-2">
                        <Target className="w-4 h-4 text-cyber-cyan" />
                        <span className="text-text-primary">Funding Request</span>
                        <span className={cn("text-xs px-2 py-1 rounded", selectedPriority.color)}>
                          {selectedPriority.label} Priority
                        </span>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm text-text-muted">Description</div>
                      <div className="text-text-secondary text-sm bg-dark-surface/50 p-3 rounded-cyber max-h-32 overflow-y-auto">
                        {formData.description}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-text-muted">Recipient</div>
                        <div className="text-text-primary font-mono text-sm break-all">{formData.recipient}</div>
                      </div>
                      
                      <div>
                        <div className="text-sm text-text-muted">Amount</div>
                        <div className="text-cyber-green font-mono text-lg">{formData.amount} {tokenSymbol}</div>
                      </div>
                    </div>

                    {formData.tags.length > 0 && (
                      <div>
                        <div className="text-sm text-text-muted mb-2">Tags</div>
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.map((tag) => (
                            <span
                              key={tag}
                              className="flex items-center space-x-1 px-2 py-1 bg-cyber-cyan/20 text-cyber-cyan rounded text-sm"
                            >
                              <Hash className="w-3 h-3" />
                              <span>{tag}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="glass-card bg-cyber-cyan/5 p-4">
                  <div className="flex items-start space-x-2">
                    <Info className="w-4 h-4 text-cyber-cyan flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-text-muted">
                      <div className="font-medium text-cyber-cyan mb-1">Important Information</div>
                      <ul className="space-y-1">
                        <li>• Voting period: 24 hours</li>
                        <li>• Quorum required: 30% of total shares</li>
                        <li>• Proposal threshold: 1 SOL in shares</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {!connected && (
                  <div className="glass-card bg-status-warning/10 border-status-warning/20 p-4">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="w-5 h-5 text-status-warning flex-shrink-0" />
                      <span className="text-status-warning text-sm">
                        Connect your wallet to create a proposal
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between space-x-3 p-6 border-t border-dark-border/30">
            <button
              onClick={step === 1 ? handleClose : previousStep}
              disabled={confirming}
              className="cyber-button flex items-center space-x-2 disabled:opacity-50"
            >
              {step === 1 ? 'Cancel' : 'Previous'}
            </button>
            
            <div className="flex items-center space-x-2">
              {step === 1 ? (
                <button
                  onClick={nextStep}
                  disabled={confirming}
                  className="cyber-button-primary flex items-center space-x-2 px-6 py-2 disabled:opacity-50"
                >
                  <span>Next</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!connected || confirming || loading}
                  className="cyber-button-primary flex items-center space-x-2 px-6 py-2 disabled:opacity-50"
                >
                  {confirming || loading ? (
                    <>
                      <Clock className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Create Proposal</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Loading Overlay */}
          {(confirming || loading) && (
            <div className="absolute inset-0 bg-dark-bg/50 backdrop-blur-sm rounded-cyber flex items-center justify-center">
              <div className="glass-card p-6 text-center">
                <div className="w-12 h-12 border-2 border-cyber-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <div className="text-cyber-cyan font-medium">
                  {confirming ? 'Creating proposal...' : 'Processing...'}
                </div>
                <div className="text-sm text-text-muted mt-1">
                  Please confirm in your wallet
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default CreateProposalModal